import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGO =
  "https://customer-assets-v7afamib.emergentagent.net/job_edju-command/artifacts/tmtoc27k_edju-cat-type-logo-png.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.forgotPassword(email.trim());
    } catch (err) {
      // Response is intentionally generic — never reveal whether the account exists.
      // Log for diagnostics only; never surfaced to the user.
      console.debug("forgot-password request error (suppressed by design):", err);
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F6FB] px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="rounded-2xl bg-[#14245F] px-5 py-3">
            <img src={LOGO} alt="Edju Cat" className="h-8 w-auto" />
          </div>
          <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.22em] text-[#1D4ED8]">War Room</p>
        </div>

        <div className="rounded-3xl border border-[#E4E9F3] bg-white p-7 shadow-[0_10px_40px_rgba(20,36,95,0.10)]">
          {sent ? (
            <div data-testid="forgot-success" className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#16A34A]/12">
                <CheckCircle2 size={24} className="text-[#16A34A]" />
              </div>
              <h1 className="font-heading text-xl font-bold text-[#14245F]">Check your inbox</h1>
              <p className="mt-2 text-sm text-[#5A6B8C]">
                If an account exists for that email, we've sent a link to reset your password. The link expires in 1 hour.
              </p>
              <a
                href="/login"
                data-testid="back-to-login"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D4ED8] hover:text-[#14245F]"
              >
                <ArrowLeft size={15} /> Back to sign in
              </a>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-form">
              <div className="mb-6 flex items-center gap-2 text-sm font-semibold text-[#5A6B8C]">
                <Mail size={15} className="text-[#1D4ED8]" />
                <span>Reset your password</span>
              </div>
              <p className="mb-4 text-sm text-[#5A6B8C]">
                Enter the email for your account and we'll send you a secure link to set a new password.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold text-[#14245F]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="forgot-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@edjucat.com"
                  autoComplete="email"
                  required
                  className="border-[#E4E9F3] bg-[#F7F9FE] focus-visible:ring-[#1D4ED8]"
                />
              </div>
              <Button
                type="submit"
                data-testid="forgot-submit"
                disabled={busy}
                className="mt-6 w-full rounded-full bg-[#1D4ED8] py-5 font-bold text-white hover:bg-[#1A45C0]"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Send reset link"}
              </Button>
              <div className="mt-4 text-center">
                <a href="/login" data-testid="back-to-login" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1D4ED8] hover:text-[#14245F]">
                  <ArrowLeft size={13} /> Back to sign in
                </a>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
